import React, { useState, useEffect, useCallback } from 'react';
import { useShortDistance, SHORT_DISTANCE_MODE } from '@this/lib/hooks/useShortDistance';
import { reportError } from '@this/lib/bugsnag';
import type Transit from '../../../../../domain/transit/transit';
import type { Tab } from '../../../../../domain/select_store';
import type SelectStore from '../../../../../domain/select_store';
import type SearchQueryItem from '../../../../../domain/search_query_item';

import TransportBoxShortDistanceContainer from './TransportBoxShortDistanceContainer';
import {
  calculateTotalPrice,
  extractAirLegInfo,
  extractNewLegInfo,
  combineAllIndexes,
  generateNewIndexes,
  findLegSummaryIndex,
  findLegSummaryByType,
  getAirOnlyLegs
} from './utils';
import { requestShortDistanceData } from './api';
import TransportBoxShortDistanceListContainer from './TransportBoxShortDistanceListContainer';

interface TransportBoxShortDistanceProps {
  queryItem?: SearchQueryItem;
  handleClickTab?: (tab: Tab) => void;
  handleSelect?: (t: Transit, opts?: { changeable?: boolean; domesticAirPriceIndex?: number }) => void;
  transit: Transit;
  inList: boolean;
  selected: boolean;
  domesticAirPriceIndex: number;
  tab?: 'outword' | 'homeword' | 'packageOutword' | 'packageHomeword' | number; // これTabって名前おかしくない？あと前半の文字列ってありえるんか？
  store: SelectStore;
  parentLoading?: (loading: boolean) => void;
  isRequestForm?: boolean;
}

const TransportBoxShortDistance: React.FC<TransportBoxShortDistanceProps> = ({
  parentLoading,
  handleSelect,
  transit,
  inList,
  tab,
  selected,
  handleClickTab,
  queryItem,
  domesticAirPriceIndex,
  store,
  isRequestForm
}) => {
  const [loading, setLoading] = useState(false);
  const { shortDistanceMode } = useShortDistance();

  const updateSegmentData = useCallback(() => {
    store.needReplaceSegments = true;

    // 個別、飛行機で選択した時にコールされる。
    // 元コードでここに書かれていたapp.render()は不要な気がする。 複数人確認して問題なければ消したい。
    // その際にこの関数もcallbackじゃない形にしたい。
    // app.render();
  }, [store]);

  const processBeforeLegUpdate = useCallback(
    async (queryItemParam: SearchQueryItem, airInfo: ReturnType<typeof extractAirLegInfo>) => {
      const beforeSummary = findLegSummaryByType(transit.segments, 'before');
      if (!beforeSummary?.indexes.length) return;

      const shortDistanceResults = await requestShortDistanceData(
        queryItemParam,
        '',
        airInfo.fromName,
        airInfo.fromTime,
        'arrival',
        true
      );

      const currentSegment = transit.segments[0];
      if (currentSegment.leg_summaries == null) return; // 型エラー対応。findLegSummaryByType() がnullじゃないならここがnullになることはないはず？

      if (shortDistanceResults[0]?.segments) {
        const resultSegment = shortDistanceResults[0].segments[0];
        if (resultSegment.leg_summaries == null) return; // 型エラー対応。findLegSummaryByType() がnullじゃないならここがnullになることはないはず？
        const newRouteInfo = extractNewLegInfo(resultSegment.leg_summaries);
        const beforeIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'before');
        const airIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'air');

        const updatedBeforeSummary = currentSegment.leg_summaries[beforeIndex];
        updatedBeforeSummary.name = newRouteInfo.name;
        updatedBeforeSummary.price = calculateTotalPrice(resultSegment.leg_summaries);
        updatedBeforeSummary.from.name = newRouteInfo.fromName;
        updatedBeforeSummary.from.time = newRouteInfo.fromTime;
        updatedBeforeSummary.to.name = newRouteInfo.toName;
        updatedBeforeSummary.to.time = newRouteInfo.toTime;
        updatedBeforeSummary.indexes = combineAllIndexes(resultSegment.leg_summaries);

        const airLegs = getAirOnlyLegs(transit.segments);
        const lastResultIndex = resultSegment.leg_summaries[resultSegment.leg_summaries.length - 1].indexes;
        const newAirIndexes = generateNewIndexes(lastResultIndex[lastResultIndex.length - 1], airLegs.length);
        currentSegment.leg_summaries[airIndex].indexes = newAirIndexes;

        currentSegment.legs = [...resultSegment.legs, ...airLegs];
        updateSegmentData();
      } else {
        const beforeIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'before');
        currentSegment.leg_summaries.splice(beforeIndex, 1);
        updateSegmentData();
      }
    },
    [transit.segments, updateSegmentData]
  );

  const processAfterLegUpdate = useCallback(
    async (queryItemParam: SearchQueryItem, airInfo: ReturnType<typeof extractAirLegInfo>) => {
      const afterSummary = findLegSummaryByType(transit.segments, 'after');
      if (!afterSummary?.indexes.length) return;

      const shortDistanceResults = await requestShortDistanceData(
        queryItemParam,
        airInfo.toName,
        '',
        airInfo.toTime,
        'departure',
        false
      );

      const currentSegment = transit.segments[0];
      if (currentSegment.leg_summaries == null) return; // 型エラー対応。findLegSummaryByType() がnullじゃないならここがnullになることはないはず？

      if (shortDistanceResults[0]?.segments) {
        const resultSegment = shortDistanceResults[0].segments[0];
        if (resultSegment.leg_summaries == null) return; // 型エラー対応。findLegSummaryByType() がnullじゃないならここがnullになることはないはず？

        const newRouteInfo = extractNewLegInfo(resultSegment.leg_summaries);
        const afterIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'after');
        const airIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'air');

        const updatedAfterSummary = currentSegment.leg_summaries[afterIndex];
        updatedAfterSummary.name = newRouteInfo.name;
        updatedAfterSummary.price = calculateTotalPrice(resultSegment.leg_summaries);
        updatedAfterSummary.from.name = newRouteInfo.fromName;
        updatedAfterSummary.from.time = newRouteInfo.fromTime;
        updatedAfterSummary.to.name = newRouteInfo.toName;
        updatedAfterSummary.to.time = newRouteInfo.toTime;

        const airSummaryIndexes = currentSegment.leg_summaries[airIndex].indexes;
        updatedAfterSummary.indexes = generateNewIndexes(
          airSummaryIndexes[airSummaryIndexes.length - 1],
          resultSegment.legs.length
        );

        const beforeSummary = findLegSummaryByType(transit.segments, 'before');
        const previousLegs = beforeSummary?.indexes.length
          ? currentSegment.legs
          : getAirOnlyLegs(transit.segments);

        currentSegment.legs = [...previousLegs, ...resultSegment.legs];
        updateSegmentData();
      } else {
        const afterIndex = findLegSummaryIndex(currentSegment.leg_summaries, 'after');
        currentSegment.leg_summaries.splice(afterIndex, 1);
        updateSegmentData();
      }
    },
    [transit.segments, updateSegmentData]
  );

  const replaceLegSummaries = useCallback(async () => {
    if (!queryItem) return;

    const airInfo = extractAirLegInfo(transit.segments[0].leg_summaries);

    await Promise.all([processBeforeLegUpdate(queryItem, airInfo), processAfterLegUpdate(queryItem, airInfo)]);
  }, [queryItem, transit.segments, processBeforeLegUpdate, processAfterLegUpdate]);

  const handleDomesticAirPriceChange = useCallback(
    async (index: number) => {
      parentLoading?.(true);
      setLoading(true);

      try {
        if (shortDistanceMode === SHORT_DISTANCE_MODE.LIMITED) {
          // limitedモードでは短距離検索をスキップして航空情報のみ使用
          handleSelect?.(transit, { domesticAirPriceIndex: index });
        } else {
          // 通常モードでは従来通りの再検索処理
          await replaceLegSummaries();
          handleSelect?.(transit, { domesticAirPriceIndex: index });
        }
      } finally {
        setLoading(false);
        parentLoading?.(false);
      }
    },
    [shortDistanceMode, replaceLegSummaries, parentLoading, handleSelect, transit]
  );

  const handleChange = useCallback(async () => {
    parentLoading?.(true);

    try {
      if (inList) {
        if (handleSelect) {
          setLoading(true);
          await handleSelect(transit);
          setLoading(false);
        }
      } else if (tab !== undefined) {
        const newTab = selected ? 'none' : tab;
        handleClickTab?.(newTab);
      }
    } finally {
      parentLoading?.(false);
    }
  }, [inList, handleSelect, transit, tab, selected, handleClickTab, parentLoading]);

  useEffect(() => {
    const shouldUpdate = inList && selected && transit.air;
    if (shouldUpdate && shortDistanceMode !== SHORT_DISTANCE_MODE.LIMITED) {
      replaceLegSummaries();
    }
  }, [inList, selected, transit.air, replaceLegSummaries, shortDistanceMode]);

  try {
    return inList ? (
      <TransportBoxShortDistanceListContainer
        transit={transit}
        selected={selected}
        domesticAirPriceIndex={domesticAirPriceIndex}
        loading={loading}
        store={store}
        handleDomesticAirPriceChange={handleDomesticAirPriceChange}
        handleChange={handleChange}
      />
    ) : (
      <TransportBoxShortDistanceContainer
        transit={transit}
        selected={selected}
        domesticAirPriceIndex={domesticAirPriceIndex}
        store={store}
        isRequestForm={isRequestForm}
        handleChange={handleChange}
      />
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default TransportBoxShortDistance;
